/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LanguageSelector } from "../components/LanguageSelector";
import { CONTACT_ID } from "../landing/contact/Contact";
import { FEATURES_ID } from "../landing/features/Features";
import { SocialMediaIcons } from "../landing/footer/SocialMediaIcons";
import { HOW_TO_ID } from "../landing/howTo/HowTo";
import { PRICING_ID } from "../landing/pricing/Pricing";
import { withMyTheme } from "../theme/theme";
import { isMobile } from "../util/isMobile";
import { isTablet } from "../util/isTablet";
import { NavigationMobile } from "./NavigationMobile";

export const NavItems = [
    {
        id: HOW_TO_ID,
        getText: (t: any) => t('navigation.howTo')
    },
    {
        id: FEATURES_ID,
        getText: (t: any) => t('navigation.features')
    },
    {
        id: PRICING_ID,
        getText: (t: any) => t('navigation.pricing')
    },
    {
        id: CONTACT_ID,
        getText: (t: any) => t('navigation.contact')
    },
]

const NavStyle = withMyTheme((theme) => css`
    position: fixed;
    top: 50px;
    left: 50px;
    display: flex;
    gap: 30px;
    align-items: center;
    z-index: 10;
    transition: opacity 0.5s ease, visibility 0.5s ease;
`);

const NavLinksStyle = withMyTheme(() => css`
    display: flex;
    align-items: center;
    gap: 30px;
`);

const NavLinkStyle = withMyTheme((theme) => css`
    color: ${theme.palette.primary.main};
    text-decoration: none;
    font-size: 22px;
    font-weight: 700;
    cursor: pointer;
    padding: 5px 0;
    font-family: ${theme.typography.body1.fontFamily};
    position: relative;
    transition: all 0.3s ease;

    &::before,
    &::after {
        content: '';
        position: absolute;
        width: 0;
        height: 2px;
        background-color: ${theme.palette.primary.main};
        transition: width 0.3s ease;
    }

    &::before {
        left: 0;
        top: 0;
    }

    &::after {
        right: 0;
        bottom: 0;
    }

    &:hover {
        transform: translateY(-2px);
        text-shadow: 0 0 8px rgba(255, 255, 255, 0.6);

        &::before,
        &::after {
            width: 100%;
        }
    }

    &:active {
        transform: translateY(1px);
    }
`);

const RightSectionStyle = withMyTheme(() => css`
    position: fixed;
    right: 50px;
    display: flex;
    align-items: center;
    gap: 30px;
`);

const LanguageSelectorStyle = withMyTheme(() => css`
    display: flex;
    align-items: center;
`);

export const Navigation = () => {
    const [mobile, setIsMobile] = useState(isMobile());
    const [visible, setVisible] = useState(true);
    const { t } = useTranslation();
    const lastScrollY = useRef(0);
    const ticking = useRef(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(isMobile());
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            if (!ticking.current) {
                window.requestAnimationFrame(() => {
                    const howToSection = document.getElementById(HOW_TO_ID);
                    
                    if (howToSection) {
                        const howToPosition = howToSection.getBoundingClientRect();
                        const windowHeight = window.innerHeight;
                        
                        // Start fading out when the HowTo section is approaching the viewport
                        // Fully invisible when the section is 20% into the viewport
                        const fadeThreshold = windowHeight * 0.8;
                        
                        if (howToPosition.top < fadeThreshold) {
                            // Calculate opacity based on how far the section has entered the viewport
                            const opacity = Math.max(0, (howToPosition.top / fadeThreshold));
                            setVisible(opacity > 0);
                        } else {
                            setVisible(true);
                        }
                    }
                    
                    lastScrollY.current = window.scrollY;
                    ticking.current = false;
                });
                
                ticking.current = true;
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToSection = (id: string) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    if (mobile) {
        return <NavigationMobile />;
    }

    const navStyleWithVisibility = withMyTheme((theme) => css`
        position: fixed;
        top: 50px;
        left: 50px;
        display: flex;
        gap: 30px;
        align-items: center;
        z-index: 10;
        transition: opacity 0.5s ease, visibility 0.5s ease;
        opacity: ${visible ? 1 : 0};
        visibility: ${visible ? 'visible' : 'hidden'};
    `);

    return (
        <nav css={navStyleWithVisibility}>
            {isTablet() || isMobile() ? <NavigationMobile /> : <>
                <div css={NavLinksStyle}>
                    {NavItems.map((item) => (
                        <span key={item.id} css={NavLinkStyle} onClick={() => scrollToSection(item.id)}>{item.getText(t)}</span>
                    ))}
                </div>
                <div css={RightSectionStyle}>
                    <div css={LanguageSelectorStyle}>
                        <LanguageSelector />
                    </div>
                    <SocialMediaIcons />
                </div>
            </>}
        </nav>
    );
}